import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  getUserConnectionsApiV1UsersMeConnectionsGet,
  listAccountsApiV1AccountsGet,
  switchToAccountApiV1AccountsSwitchAccountPost,
} from "@/backend-sdk/client/sdk.gen";
import type {
  ConnectionInfo,
  ManagedAccountListItem,
  ProfileAccount,
} from "@/backend-sdk/client/types.gen";
import {
  CABECALHO_403_ESPERADO,
  clearHeyApiSubAccountToken,
  contaAtivaDoToken,
  getHeyApiSubAccountToken,
  heyAdminApiClient,
  isTokenExpired,
  setHeyApiSubAccountToken,
} from "@/lib/hey-api-client";
import { authDbg } from "@/lib/auth-debug-logger";
import {
  accountAccessContextFor,
  ehDonoDasContas,
  ehPlanoDeAgencia,
  getAccountAccessContext,
  isAgencyAccessContext,
  isMultiAccountAccessContext,
  setAccountAccessContext,
} from "@/lib/account-access-context";
import {
  escreveNaLoja,
  lojasAbriveisDoMembro,
  navegaPorEscopoCedido,
} from "@/lib/team-access";
import { useAuth } from "./auth.context";
import { toast } from "@/hooks/use-toast";

/* eslint-disable react-refresh/only-export-components */

/**
 * Há token de subconta válido em mãos — ou seja, esta sessão já registrou uma loja ativa.
 *
 * <M-216> Este contexto **não** publica mais isso como `isSubaccount`. O campo existia para
 * um consumidor só, o gate das abas de cobrança da Minha Conta, e ele estava errado: com
 * <D-062> o token de troca de conta deixou de ser emitido no nome do usuário-dono da loja,
 * então as rotas de `/my-account/**` continuam respondendo a cobrança de quem está logado
 * depois da troca. Quem decide aquelas abas passou a ser `ehAssinante`, sobre o perfil.
 *
 * Publicar de novo "estou dentro de uma loja" convidaria a mesma confusão: dentro de uma
 * loja é onde o lojista multi-conta vive o tempo todo, e nunca foi uma perda de direito.
 */
const hasValidSubAccountToken = () => {
  const token = getHeyApiSubAccountToken();
  return !!token && !isTokenExpired(token);
};

export type Account = Partial<ManagedAccountListItem | ProfileAccount> & {
  id: string;
  name: string;
  account_id?: string;
  slug?: string;
  status?: string | null;
  role?: string | null;
  cnpj?: string | null;
  customer_uuid?: string | null;
};

export interface SwitchAccountOptions {
  /** Navega para /dashboard (ou /integrations) após a troca. Padrão: true. */
  redirect?: boolean;
}

/** Persistência da escolha "Conta Principal" vs subconta para não reabrir o
 * seletor automaticamente após reload. */
const ACCOUNT_SELECTION_KEY = "account_selection";

interface AccountContextType {
  selectedAccount: Account | null;
  accounts: Account[];
  loading: boolean;
  switchAccount: (
    accountId: string,
    options?: SwitchAccountOptions,
  ) => Promise<void>;
  loadAccounts: () => Promise<void>;
  clearSelectedAccount: (options?: SwitchAccountOptions) => void;
  isAgencyUser: boolean;
  shouldForceSelector: boolean;
  /**
   * <M-214> O usuário tem mais de uma conta própria — por plano de agência ou não — e por
   * isso o seletor de contas deve aparecer. É este o gate da interface de troca de conta;
   * `isAgencyUser` sobrou para o que é de fato específico de agência (o vocabulário das
   * telas e os créditos de IA da conta principal).
   */
  canSwitchAccounts: boolean;
  /** true quando o usuário escolheu explicitamente a Conta Principal. */
  hasChosenMainAccount: boolean;
  /**
   * As lojas desta tela vêm de um **time**, e não de contas próprias.
   *
   * É o mesmo predicado que liga o modo somente leitura no contexto de autenticação, e está
   * republicado aqui porque quem precisa dele são os componentes de conta — o seletor e o
   * modal —, que já consomem este contexto e não têm por que passar a depender do outro só
   * para trocar um rótulo.
   */
  isTeamScoped: boolean;
  /**
   * A loja **ativa** recusa escrita de quem está olhando?
   *
   * <M-248> Vinha do contexto de autenticação e era o mesmo booleano de `isTeamScoped`:
   * navegar por escopo cedido *era* ser somente leitura, porque a trilha recusava toda
   * escrita. <D-061> separou as duas coisas — quem administra a conta pode promover a
   * pessoa a `editor`, e aí ela escreve na loja que acompanha —, e a resposta passou a ser
   * por **loja**, que é o que este contexto sabe e o outro não.
   *
   * Quem responde é o servidor: `can_write`, em `GET /api/v1/teams/my-scope`, é a barreira
   * avaliada para cada loja do escopo. Enquanto a loja ativa não está escolhida, a resposta
   * é `true` — esconder o botão até saber custa menos do que oferecê-lo e receber `403`.
   *
   * Continua sendo cortesia com quem usa, e nunca a proteção: quem recusa de verdade é
   * `TenantGuardService`, no `pde-api-server`, esteja a tela escondendo o botão ou não.
   */
  isReadOnly: boolean;
  /**
   * `isReadOnly` já é a resposta final, ou ainda é o palpite fechado do primeiro instante?
   *
   * <M-248> A distinção existe porque as duas coisas pedem tratamentos opostos. Para
   * **esconder** um botão, o palpite fechado serve: some agora, aparece daqui a um quadro.
   * Para **desviar de rota**, não: o `<Navigate>` já está confirmado quando a resposta
   * chega, e o `editor` que recarregou a página em `/dashboard/integrations` acabaria no
   * painel sem entender por quê.
   *
   * `loading` não responde isto: ele vai a `false` no ramo `!user`, que roda na montagem do
   * app, e nunca volta a `true` quando o perfil chega. Quem sabe que o escopo assentou é a
   * loja ativa ter sido escolhida. Achado de code review.
   */
  acessoResolvido: boolean;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error("useAccount deve ser usado dentro de um AccountProvider");
  }
  return context;
};

interface AccountProviderProps {
  children: ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = ({
  children,
}) => {
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, teamScope } = useAuth();
  const navigate = useNavigate();

  /**
   * Quem navega por **escopo cedido**, que é o quarto caso deste contexto e o único que não
   * troca de token ao trocar de loja.
   *
   * São dois: o **funcionário** de um time da agência e a **credencial de cliente**, criada
   * em "Usuários" com o tipo *Cliente* (D-060). Os dois enxergam um conjunto de lojas em
   * leitura, e por isso usam o mesmo caminho aqui.
   *
   * A lista deles não vem de `GET /api/v1/accounts` nem de `user.accounts`: vem de
   * `GET /api/v1/teams/my-scope`, a única resposta que traz o `customer_uuid` das lojas que
   * eles alcançam. As outras duas os recusariam ou os enganariam — `/api/v1/accounts`
   * responde `403 Usuário não é um assinante` a quem não tem perfil de assinante, e
   * `user.accounts` traz o vínculo cru: para o funcionário, a conta principal da agência,
   * que não é loja de cliente nenhuma; para a credencial de cliente, as lojas dela **sem**
   * o `customer_uuid`, que é justamente o que as telas mandam.
   */
  const ehMembro = navegaPorEscopoCedido(user, teamScope);
  const lojasDoMembro: Account[] = lojasAbriveisDoMembro(teamScope).map(
    (loja) => ({
      id: loja.account_id,
      name: loja.name,
      slug: loja.slug,
      // `status: null` e um papel de leitura: é a mesma forma do ramo "usuário de conta" de
      // `/users/me`, e é o que impede qualquer tela de tratá-lo como dono
      // (`ehDonoDasContas`).
      status: null,
      role: "Somente leitura",
      customer_uuid: loja.customer_uuid,
    }),
  );

  const [isAgencyUser, setIsAgencyUser] = useState(false);
  const [canSwitchAccounts, setCanSwitchAccounts] = useState(false);
  const [shouldForceSelector, setShouldForceSelector] = useState(false);
  const [hasChosenMainAccount, setHasChosenMainAccount] = useState(
    () => localStorage.getItem(ACCOUNT_SELECTION_KEY) === "main",
  );

  const userAccounts: Account[] = (user?.accounts ?? []).map((account) => ({
    ...account,
  }));

  useEffect(() => {
    if (!user) {
      authDbg("[AUTH_DBG] AccountContext: user is null, resetting state");
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedAccount(null);
      setAccounts([]);
      setLoading(false);
      setHasChosenMainAccount(false);
      return;
    }

    if (ehMembro) {
      // O membro nunca passa por `loadAccounts` nem por `switchAccount` com API: as duas
      // dependem de ser assinante. Tudo que ele precisa já veio em `my-scope`.
      setAccounts(lojasDoMembro);
      setIsAgencyUser(false);
      // Mais de uma loja no escopo ⇒ o seletor aparece. Com uma só não há o que escolher,
      // e o modal seria uma pergunta de resposta única.
      setCanSwitchAccounts(lojasDoMembro.length > 1);
      // Nunca força o modal: ao contrário do dono, ele não decide nada ao entrar — o
      // contexto de autenticação já escolheu a loja padrão, e o seletor fica no cabeçalho
      // para quem quiser trocar.
      setShouldForceSelector(false);

      const ativaId = localStorage.getItem("active_subaccount_id");
      const ativa =
        lojasDoMembro.find((loja) => loja.id === ativaId) ??
        lojasDoMembro[0] ??
        null;
      setSelectedAccount(ativa);
      setLoading(false);
      return;
    }

    const planCategory = user?.subscription?.plan_category;
    const isAgencyPlan = ehPlanoDeAgencia(planCategory);
    const hasSubAccountToken = hasValidSubAccountToken();
    const accessContext = getAccountAccessContext();
    const isAgencyContext = isAgencyAccessContext(accessContext);
    // <M-214> O contexto persistido de quem já trocou de conta, agência ou não. Depois da
    // troca o perfil pode vir da subconta (uma conta só, sem assinatura), e é esta marca
    // que impede o seletor de sumir no reload.
    const isMultiAccountContext = isMultiAccountAccessContext(accessContext);
    const hasSubaccounts =
      Array.isArray(user?.accounts) && user.accounts.length > 0;
    // <M-214> Mais de uma conta **própria**: é o que dá direito ao seletor. Todo plano
    // `lojista` permite até cinco contas, e quem tinha três via só a primeira.
    const hasMultipleAccounts =
      Array.isArray(user?.accounts) &&
      user.accounts.length > 1 &&
      ehDonoDasContas(user.accounts);
    // O perfil em mãos é o do dono das contas, e não o da subconta em que se entrou? É o
    // que autoriza usar `user.accounts` como a lista de contas; sem isso, quem manda é o
    // `loadAccounts`, que sempre consulta com o token do dono.
    const perfilDoDono = isAgencyPlan || hasMultipleAccounts;

    authDbg("[AUTH_DBG] AccountContext: User data:", {
      planCategory,
      isAgencyPlan,
      hasSubAccountToken,
      accessContext,
      isAgencyContext,
      userType: user?.user_type,
      hasAccounts: hasSubaccounts,
      accountsCount: user?.accounts?.length || 0,
      accountsIsArray: Array.isArray(user?.accounts),
      accountsIsUndefined: user?.accounts === undefined,
      hasMultipleAccounts,
      isMultiAccountContext,
    });

    // Manter isAgencyUser = true se:
    // 1. O plano é de agência, OU
    // 2. O contexto de acesso indica que é uma agência/subconta de agência
    // Isso garante que o seletor de contas permaneça visível mesmo após selecionar uma subconta
    const effectiveIsAgency = isAgencyPlan || isAgencyContext;
    setIsAgencyUser(effectiveIsAgency);

    // <M-214> Quem manda no seletor é ter mais de uma conta própria — pelo plano de
    // agência, pelo contexto persistido ou pela contagem do próprio perfil.
    const podeTrocarDeConta =
      effectiveIsAgency || hasMultipleAccounts || isMultiAccountContext;
    setCanSwitchAccounts(podeTrocarDeConta);

    // Forçar o seletor se:
    // 1. Tem contas para escolher, OU
    // 2. Já há uma conta selecionada por cima da do dono
    setShouldForceSelector(
      podeTrocarDeConta && (hasSubaccounts || isMultiAccountContext),
    );

    if (podeTrocarDeConta) {
      // Usuário com mais de uma conta (por plano de agência, por contagem ou por contexto)
      if (isMultiAccountContext && hasSubAccountToken) {
        // Conta já selecionada (token existe)
        // Não sobrescrever accounts/selectedAccount com dados do perfil da subconta
        // As contas serão carregadas via loadAccounts() usando adminApi
        console.log(
          "🔍 Conta selecionada ativa - preservando estado, contas via loadAccounts",
        );
        const activeSubaccountId = localStorage.getItem("active_subaccount_id");
        if (activeSubaccountId && !selectedAccount) {
          // Tentar restaurar a conta selecionada a partir das contas do user (se disponíveis)
          if (hasSubaccounts) {
            const matchedAccount = userAccounts.find(
              (acc) => acc.id === activeSubaccountId,
            );
            if (matchedAccount) {
              const customerUuid =
                localStorage.getItem("active_customer_uuid") ||
                matchedAccount.customer_uuid;
              const accountWithCustomerUuid = {
                ...matchedAccount,
                customer_uuid: customerUuid,
              };
              setSelectedAccount(accountWithCustomerUuid);
            }
          }
          // Se não encontrou nas contas do user (perfil da subconta não tem),
          // o loadAccounts vai buscar via adminApi e restaurar
        }
        if (hasSubaccounts) {
          setAccounts(userAccounts);
        }
        // Se accounts está vazio (perfil da subconta), loadAccounts vai preencher
      } else if (perfilDoDono && !hasSubaccounts) {
        // Dono sem contas: limpar seleção e não auto-selecionar
        console.log("🔍 Nenhuma conta disponível - limpando seleção");
        setSelectedAccount(null);
        setAccounts([]);
        localStorage.removeItem("active_subaccount_id");
        localStorage.removeItem("active_customer_uuid");
      } else if (perfilDoDono && hasSubaccounts) {
        // Tem contas, mas nenhuma selecionada ainda: o seletor pede a escolha
        console.log("🔍 Contas disponíveis, aguardando seleção manual");
        setAccounts(userAccounts);
      } else {
        // Contexto preservado mas sem dados do perfil (ex: reload com token de subconta)
        // Manter estado atual e deixar loadAccounts preencher
        console.log(
          "🔍 Contexto de contas preservado, aguardando loadAccounts",
        );
      }
    } else {
      // Usuário com uma conta só: ela é a conta ativa, e não há o que escolher
      console.log("🔍 Configurando conta única do usuário");
      setAccounts(userAccounts);
      if (!selectedAccount && userAccounts.length > 0) {
        const defaultAccount = userAccounts[0];
        const customerUuid =
          localStorage.getItem("active_customer_uuid") ||
          defaultAccount.customer_uuid;
        const accountWithCustomerUuid = {
          ...defaultAccount,
          customer_uuid: customerUuid,
        };
        setSelectedAccount(accountWithCustomerUuid);
        if (defaultAccount?.id) {
          localStorage.setItem("active_subaccount_id", defaultAccount.id);
        }
        if (customerUuid) {
          localStorage.setItem("active_customer_uuid", customerUuid);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, teamScope]);

  const loadAccounts = async () => {
    // `GET /api/v1/accounts` responde `403 Usuário não é um assinante` para o membro, e as
    // lojas dele já estão em mãos desde `my-scope`.
    if (ehMembro) {
      setAccounts(lojasDoMembro);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log(
        "[AUTH_DBG] loadAccounts: fetching accounts with heyAdminApiClient",
      );
      // Usar adminApi para sempre buscar contas da agência (não da subconta)
      const { data: response } = await listAccountsApiV1AccountsGet({
        client: heyAdminApiClient,
        throwOnError: true,
      });
      const accountList: Account[] = (response.accounts || []).map(
        (account) => ({
          ...account,
        }),
      );
      setAccounts(accountList);
      console.log(
        `[AUTH_DBG] loadAccounts: loaded ${accountList.length} accounts`,
      );

      // Restaurar a conta selecionada se há uma subconta ativa no localStorage
      const hasSubAccountToken = hasValidSubAccountToken();
      const activeAccountId = localStorage.getItem("active_subaccount_id");
      console.log(
        `[AUTH_DBG] loadAccounts: hasSubAccountToken=${hasSubAccountToken} activeAccountId=${activeAccountId} selectedAccount=${selectedAccount?.id || "null"}`,
      );
      if (hasSubAccountToken && activeAccountId && !selectedAccount) {
        const matchedAccount = accountList.find(
          (acc: Account) => acc.id === activeAccountId,
        );
        console.log(
          `[AUTH_DBG] loadAccounts: matchedAccount from accountList=${matchedAccount ? matchedAccount.id : "NOT_FOUND"}`,
        );
        if (matchedAccount) {
          const customerUuid =
            localStorage.getItem("active_customer_uuid") ||
            matchedAccount.customer_uuid;
          setSelectedAccount({
            ...matchedAccount,
            customer_uuid: customerUuid,
          });
          console.log(
            `[AUTH_DBG] loadAccounts: restored selectedAccount: ${matchedAccount.id}`,
          );
        }
      }
    } catch (error) {
      authDbg(`[AUTH_DBG] loadAccounts: error: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  // Carregar contas ao iniciar
  useEffect(() => {
    if (canSwitchAccounts && shouldForceSelector) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadAccounts();
    } else if (!canSwitchAccounts && user?.accounts) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSwitchAccounts, shouldForceSelector, user?.accounts]);

  /**
   * <D-062> A conta ativa mora no **token**, e restaurar a loja do navegador não a põe lá.
   *
   * A loja escolhida sobrevive ao reload em `active_subaccount_id`, e o contexto a devolve
   * para a tela. O claim `account_id`, não: só `POST /accounts/switch-account` o grava. Sem
   * ele, toda rota que não recebe `customer_uuid` — o estado das conexões, a lista de contas
   * externas, as telas de integração — responde `400 Conta não informada` na **primeira**
   * tela depois do login, antes de o usuário clicar em loja nenhuma.
   *
   * Foi o que o teste de ponta a ponta pegou. Este efeito fecha a lacuna: sempre que a loja
   * ativa da tela e a do token divergirem, ele pede um token novo — uma chamada, e só quando
   * há divergência.
   *
   * A loja que não está `active` fica de fora: a do cadastro nasce `inactive` e só vira
   * `active` no fim do checkout, e o servidor recusa a troca com `400 Conta inativa não pode
   * ser selecionada` — que o interceptor mostrava como toast a cada reload da página de
   * planos. `status` nulo (as lojas do membro) não diz nada, e a sincronização segue.
   */
  const statusDaLojaAtiva = selectedAccount?.status;
  useEffect(() => {
    const lojaAtiva = selectedAccount?.id;
    if (!lojaAtiva || contaAtivaDoToken() === lojaAtiva) return;
    if (statusDaLojaAtiva && statusDaLojaAtiva !== "active") return;

    void (async () => {
      try {
        const { data } = await switchToAccountApiV1AccountsSwitchAccountPost({
          body: { account_id: lojaAtiva },
          throwOnError: true,
        });
        if (data.subaccount_token) {
          setHeyApiSubAccountToken(data.subaccount_token);
        }
      } catch (error) {
        // Sem o token novo, as rotas que recebem `customer_uuid` continuam funcionando: a
        // tela não fica em branco por causa disto.
        authDbg(
          `[AUTH_DBG] AccountContext: não foi possível registrar a conta ativa no token: ${String(error)}`,
        );
      }
    })();
  }, [selectedAccount?.id, statusDaLojaAtiva]);

  const switchAccount = async (
    accountId: string,
    options?: SwitchAccountOptions,
  ) => {
    const shouldRedirect = options?.redirect !== false;

    // <D-062> A troca do membro passou a chamar a API, como a da agência.
    //
    // Ela era local — nenhum token novo, nenhuma chamada — porque
    // `POST /accounts/switch-account` emitia uma credencial **do dono da loja**, e isso daria
    // ao membro escrita total nela. O token não finge mais ser ninguém: ele continua sendo o
    // do membro e ganha o claim `account_id`, que diz qual é a loja ativa. Quem decide o que
    // ele pode fazer ali é o `access_level` dele, conferido a cada requisição.
    //
    // O que se ganha com a chamada é justamente esse claim: as rotas que **não** recebem
    // `customer_uuid` — conectar integração, listar contas externas — resolviam o tenant pelo
    // token, e para o membro caíam na pessoa em vez da loja.
    if (ehMembro) {
      const loja = lojasDoMembro.find((item) => item.id === accountId);
      if (!loja) return;

      try {
        const { data: switchResponse } =
          await switchToAccountApiV1AccountsSwitchAccountPost({
            body: { account_id: accountId },
            throwOnError: true,
          });
        if (switchResponse.subaccount_token) {
          setHeyApiSubAccountToken(switchResponse.subaccount_token);
        }
      } catch (error) {
        // A troca de loja do membro nunca dependeu do servidor, e continua não dependendo:
        // sem o token novo ele ainda navega mandando `customer_uuid` em cada requisição. O
        // que fica de fora são as rotas que não o recebem.
        console.warn("Erro ao registrar a loja ativa no token:", error);
      }

      localStorage.setItem("active_subaccount_id", loja.id);
      localStorage.setItem(
        "active_customer_uuid",
        loja.customer_uuid ?? loja.id,
      );
      localStorage.setItem(ACCOUNT_SELECTION_KEY, "subaccount");
      setSelectedAccount(loja);
      setHasChosenMainAccount(false);

      toast({
        title: "Loja selecionada",
        description: `Visualizando dados de: ${loja.name}`,
      });
      if (shouldRedirect) navigate("/dashboard");
      return;
    }

    try {
      console.log("🔍 switchAccount: Iniciando switch para conta:", accountId);
      console.log(
        "🔍 switchAccount: Contas disponíveis no contexto:",
        Array.isArray(accounts) ? accounts.length : "Não é array",
      );
      console.log(
        "🔍 switchAccount: Accounts é array?",
        Array.isArray(accounts),
      );

      // Encontrar a conta selecionada (pode estar no contexto ou ser buscada novamente)
      let account: Account | undefined;
      if (Array.isArray(accounts)) {
        account = accounts.find((acc) => acc.id === accountId);
        console.log("🔍 switchAccount: Conta encontrada no contexto:", account);
      }

      // Se não encontrou no contexto, buscar novamente
      if (!account) {
        console.log(
          "🔍 switchAccount: Conta não encontrada no contexto, buscando novamente...",
        );
        // Buscar todas as contas novamente
        const { data: response } = await listAccountsApiV1AccountsGet({
          client: heyAdminApiClient,
          throwOnError: true,
        });
        const allAccounts: Account[] = (response.accounts || []).map(
          (account) => ({
            ...account,
          }),
        );
        account = allAccounts.find((acc) => acc.id === accountId);

        if (!account) {
          throw new Error("Conta não encontrada");
        }
        setAccounts(allAccounts);
      }

      // O estado das conexões sai **junto** com a troca, e não depois dela. Ele decide só
      // para onde navegar, e o `customer_uuid` da loja já está em mãos antes do token novo:
      // desde <D-062> o token continua sendo da mesma pessoa, e quem confere o alcance é o
      // guard, sobre o `customer_uuid` da consulta. Em série, as duas chamadas somavam quatro
      // idas e voltas (cada uma com o seu preflight) antes de a tela sequer começar a mudar.
      //
      // O `catch` vira valor para que uma troca recusada não deixe uma rejeição solta. E o
      // `403` é declarado esperado: se a pessoa não alcança a loja, a troca é recusada e já
      // avisa por conta própria — sem o cabeçalho, esta consulta, que saiu antes de a
      // recusa chegar, somaria um segundo toast vermelho ao da troca. Achado de code review.
      const conexoesDaLoja = shouldRedirect
        ? getUserConnectionsApiV1UsersMeConnectionsGet({
            throwOnError: true,
            headers: { [CABECALHO_403_ESPERADO]: "1" },
            query: { customer_uuid: account.customer_uuid ?? accountId },
          }).then(
            ({ data }) => ({
              ok: true as const,
              connections: data.connections,
            }),
            (erro: unknown) => ({ ok: false as const, erro }),
          )
        : null;

      // Chamar a API para alternar a conta (usando adminApi para garantir que use token da agência)
      const { data: switchResponse } =
        await switchToAccountApiV1AccountsSwitchAccountPost({
          body: { account_id: accountId },
          client: heyAdminApiClient,
          throwOnError: true,
        });

      console.log("🔍 switchAccount: Resposta do servidor:", switchResponse);

      // Armazenar o token da subconta se foi retornado
      if (switchResponse.subaccount_token) {
        setHeyApiSubAccountToken(switchResponse.subaccount_token);
        // Marcar que há uma conta selecionada por cima da do dono, para o seletor
        // continuar na tela depois do reload — sem prometer agência a quem não é.
        setAccountAccessContext(
          accountAccessContextFor({
            isAgency: isAgencyUser,
            isSwitched: true,
          }),
        );
        localStorage.setItem("active_subaccount_id", accountId);

        const customerUuid =
          switchResponse.customer_uuid || account?.customer_uuid;
        if (customerUuid) {
          localStorage.setItem("active_customer_uuid", customerUuid);
          console.log("🔍 switchAccount: customer_uuid salvo:", customerUuid);
        }
      }

      // Atualizar o estado local
      const updatedAccount = { ...account } as Account;
      // Sempre configurar o customer_uuid, priorizando o que vem da API
      const customerUuid =
        switchResponse.customer_uuid || account?.customer_uuid;
      updatedAccount.customer_uuid = customerUuid;
      setSelectedAccount(updatedAccount);
      // Persistir a escolha da subconta (impede o seletor de reabrir no reload)
      localStorage.setItem(ACCOUNT_SELECTION_KEY, "subaccount");
      setHasChosenMainAccount(false);

      // Verificar se a conta tem conexões ativas — a consulta já saiu junto com a troca
      if (conexoesDaLoja) {
        const resultado = await conexoesDaLoja;
        try {
          if (!resultado.ok) throw resultado.erro;
          const connections: ConnectionInfo[] = resultado.connections || [];

          const hasActiveConnections = connections.some(
            (connection) => connection.connected === true,
          );

          if (hasActiveConnections) {
            // Redirecionar para Dashboard Visão Geral
            toast({
              title: "Conta ativada",
              description: `Visualizando dados de: ${account.name}`,
            });
            navigate("/dashboard");
          } else {
            // Redirecionar para tela de integrações
            toast({
              title: "Conta selecionada",
              description: `Configure as integrações para: ${account.name}`,
            });
            navigate("/dashboard/integrations");
          }
        } catch (integrationError) {
          console.warn("Erro ao verificar integrações:", integrationError);
          // Em caso de erro, redirecionar para dashboard
          navigate("/dashboard");
        }
      }

      // Garantir que o contexto persista para manter o seletor visível.
      // Não sobrescrever o que já foi gravado acima.
      if (!isMultiAccountAccessContext(getAccountAccessContext())) {
        setAccountAccessContext(
          accountAccessContextFor({
            isAgency: isAgencyUser,
            isSwitched: true,
          }),
        );
      }
    } catch (error) {
      console.error("Erro ao alternar conta:", error);

      // Fallback: apenas atualizar o estado local se a API falhar
      const account = accounts.find((acc) => acc.id === accountId);
      if (account) {
        // A loja do fallback é a escolhida, e não a que estava no navegador. Ler
        // `active_customer_uuid` aqui devolvia a loja **anterior**: o toast dizia "Visualizando
        // dados de: B" e as telas, sem a `key` de `ExigeLojaAtiva` mudar, seguiam em A. Achado
        // de code review.
        const customerUuid = account.customer_uuid ?? account.id;
        localStorage.setItem("active_customer_uuid", customerUuid);
        const accountWithCustomerUuid = {
          ...account,
          customer_uuid: customerUuid,
        };
        setSelectedAccount(accountWithCustomerUuid);
        localStorage.setItem(ACCOUNT_SELECTION_KEY, "subaccount");
        setHasChosenMainAccount(false);

        toast({
          title: "Conta alterada (modo local)",
          description: `Visualizando dados de: ${account.name}. Alguns dados podem não estar sincronizados.`,
          variant: "default",
        });
      }
    }
  };

  // Escutar eventos de seleção de conta
  // NOTA: Usamos um ref para acessar accounts sem adicionar como dependência
  // Isso evita re-registrar o listener a cada mudança de accounts
  const accountsRef = React.useRef(accounts);
  React.useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  useEffect(() => {
    const handleSelectAccount = (event: CustomEvent) => {
      const { accountId } = event.detail;
      if (accountId) {
        console.log(
          "🔍 AccountContext: Evento selectAccount recebido:",
          accountId,
        );
        console.log(
          "🔍 AccountContext: Contas disponíveis:",
          accountsRef.current?.length || 0,
        );
        console.log(
          "🔍 AccountContext: Accounts é array?",
          Array.isArray(accountsRef.current),
        );

        // Garantir que accounts seja um array antes de usar .find()
        if (
          Array.isArray(accountsRef.current) &&
          accountsRef.current.length > 0
        ) {
          console.log(
            "🔍 AccountContext: Accounts é array válido, prosseguindo...",
          );
          switchAccount(accountId);
        } else {
          console.warn(
            "🔍 AccountContext: Accounts vazio ou inválido, recarregando...",
          );
          // Tentar recarregar as contas
          loadAccounts()
            .then(() => {
              switchAccount(accountId);
            })
            .catch((error) => {
              console.error(
                "🔍 AccountContext: Erro ao recarregar contas:",
                error,
              );
            });
        }
      }
    };

    window.addEventListener(
      "selectAccount",
      handleSelectAccount as EventListener,
    );

    return () => {
      window.removeEventListener(
        "selectAccount",
        handleSelectAccount as EventListener,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Dependências vazias - usamos ref para acessar accounts

  const clearSelectedAccount = (options?: SwitchAccountOptions) => {
    setSelectedAccount(null);
    clearHeyApiSubAccountToken();
    // Volta ao contexto do dono, sem conta selecionada por cima — o seletor continua
    // visível, e o próximo `/users/me` não é mais buscado como se houvesse subconta.
    setAccountAccessContext(
      accountAccessContextFor({ isAgency: isAgencyUser, isSwitched: false }),
    );
    localStorage.removeItem("active_subaccount_id");
    localStorage.removeItem("active_customer_uuid");
    // Persistir a escolha da Conta Principal (impede o seletor de reabrir no reload)
    localStorage.setItem(ACCOUNT_SELECTION_KEY, "main");
    setHasChosenMainAccount(true);
    console.log(
      "🔍 Cleared selected account and customer_uuid from localStorage",
    );
    if (options?.redirect !== false) {
      // Voltar para a Conta Principal: seguir para a gestão de contas da agência.
      navigate("/dashboard/accounts");
    }
  };

  const value: AccountContextType = {
    selectedAccount,
    accounts,
    loading,
    switchAccount,
    loadAccounts,
    clearSelectedAccount,
    isAgencyUser,
    shouldForceSelector,
    canSwitchAccounts,
    hasChosenMainAccount,
    isTeamScoped: ehMembro,
    // <M-248> A pergunta é sobre a loja aberta, e não sobre a trilha por onde ela chegou.
    // Quem não navega por escopo cedido nunca é somente leitura por este caminho: o
    // assinante e o dono de loja alcançam o que é deles por vínculo próprio.
    isReadOnly: ehMembro && !escreveNaLoja(teamScope, selectedAccount?.id),
    // Quem não navega por escopo cedido já nasce resolvido; para quem navega, a resposta é
    // da loja ativa, e só existe depois que o efeito acima a escolheu. `ehMembro` exige ao
    // menos uma loja abrível, então esta condição sempre chega a ser verdadeira.
    acessoResolvido: !ehMembro || selectedAccount !== null,
  };

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
};
